import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Target, Sparkles } from "lucide-react";

export default function CoveragePage() {
  const { activeTenantId } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/content/coverage");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);

  const openGenerate = (row) => {
    navigate("/dashboard/content", {
      state: {
        prefill: {
          content_type: row.suggested_content_type,
          topic: `${row.service} in ${row.city}, ${row.state}`,
          location: row.city, state: row.state,
        },
      },
    });
  };

  if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>;

  return (
    <div className="space-y-6" data-testid="coverage-page">
      <p className="text-sm text-[#71717A] max-w-xl">
        Servicios x ciudades cruzado contra el contenido ya aprobado/programado/publicado -- qué combinación
        ya tiene contenido y cuál todavía falta.
      </p>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6 space-y-3" data-testid="coverage-summary-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-[#0055FF]" />
            <h3 className="font-heading font-bold text-[#09090B]">Cobertura</h3>
          </div>
          <span className="text-2xl font-heading font-bold text-[#0055FF]" data-testid="coverage-percentage">
            {data.coverage_pct}%
          </span>
        </div>
        <Progress value={data.coverage_pct} data-testid="coverage-progress-bar" />
        <p className="text-xs text-[#71717A]">
          {data.covered_count} de {data.total_combos} combinaciones servicio x ciudad ya tienen contenido.
        </p>
      </Card>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {data.missing.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <Target className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm text-[#71717A]">Cobertura completa: no hay combinaciones faltantes.</p>
          </div>
        ) : (
          <Table data-testid="coverage-missing-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Servicio</TableHead>
                <TableHead>Ciudad</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Prioridad</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.missing.map((m, i) => (
                <TableRow key={i} data-testid={`coverage-missing-row-${i}`}>
                  <TableCell className="font-medium">{m.service}</TableCell>
                  <TableCell>{m.city}</TableCell>
                  <TableCell className="text-[#71717A]">{m.state}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm">{m.priority}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="rounded-md" onClick={() => openGenerate(m)} data-testid={`coverage-generate-button-${i}`}>
                      <Sparkles className="h-3.5 w-3.5 mr-1" /> Generar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
